import React from 'react';
import { Star, Quote, ShieldCheck } from 'lucide-react';
import { SITE } from '../../mock';

const TESTIMONIALS = [
  {
    quote:
      'This book is truly inspiring and easy to understand. It beautifully explains how our thoughts, energy, and actions shape our life. The practical guidance makes it easy to apply in daily life.',
    name: 'Jay Thakkar',
    role: 'SAP Consultant, TCS',
  },
  {
    quote:
      'A compelling and energetically written self-help book that blends mindset psychology, manifestation philosophy, and practical financial behavior into a cohesive framework.',
    name: 'Suchit Shah',
    role: 'Co-Founder, Neo Progress · Neo Algo',
  },
  {
    quote:
      'It changed the way I think about myself and my life. The biggest realization I had is this: our thoughts truly shape where we go in life.',
    name: 'Dr. Priti Patel',
    role: 'Orion Dental Clinic',
  },
  {
    quote:
      'This book simplifies complex ideas like manifestation and energy into practical steps anyone can follow. A rare clarity.',
    name: 'T. R. Keshav',
    role: 'Fundflow Consultancy',
  },
  {
    quote:
      "This is not your typical motivational book — it's a structured guide to self-mastery. The action-oriented approach sets it apart.",
    name: 'Nidhi Chandel',
    role: 'Varuni Systems',
  },
  {
    quote:
      'The book stands out for its action-oriented approach, moving beyond abstract motivation into daily practices, assignments, and behavioural shifts.',
    name: 'Suchit Shah',
    role: 'Co-Founder, Neo Progress',
  },
];

export default function BookProof() {
  return (
    <>
      {/* Testimonials */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Real Readers · Real Results
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.05] tracking-tight">
            They opened the book skeptical.
            <br />
            <span className="italic text-amber-200">They closed it changed.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-900/50 p-7"
              >
                <Quote className="absolute top-5 right-5 text-amber-300/15" size={36} strokeWidth={1.2} />
                <div className="flex items-center gap-0.5 text-amber-300">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={13} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="mt-4 text-neutral-200 text-[14px] md:text-[14.5px] leading-relaxed">
                  “{t.quote}”
                </p>
                <div className="mt-5">
                  <p className="text-white font-serif text-lg">{t.name}</p>
                  <p className="text-neutral-500 text-xs mt-0.5">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Refund promise */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-amber-300/25 bg-gradient-to-br from-amber-300/10 via-transparent to-transparent p-8 md:p-10">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-300/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-2xl bg-amber-300/15 border border-amber-300/30 flex items-center justify-center text-amber-300">
                  <ShieldCheck size={22} strokeWidth={1.5} />
                </span>
                <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300">
                  My Promise to You
                </p>
              </div>
              <h3 className="mt-5 font-serif text-white text-[26px] md:text-[34px] leading-[1.1] tracking-tight">
                7-Day “Shift or Refund” Guarantee
              </h3>
              <p className="mt-4 text-neutral-300 text-[14.5px] md:text-[15px] leading-relaxed">
                Read the book. Do the first few assignments. Feel into it. If within 7 days you
                genuinely feel that <em>You Are The Magician</em> didn't shift your perspective on
                money, energy, and wealth — just email{' '}
                <a className="text-amber-300 hover:underline" href="mailto:info@nishitthakkar.in">
                  info@nishitthakkar.in
                </a>{' '}
                and we'll refund you in full.
              </p>
              <p className="mt-3 text-neutral-400 text-[14px] leading-relaxed">
                No arguments, no forms, no drama. I only want your money if this book genuinely
                serves you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Author */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5">
              Meet The Author
            </p>
            <h2 className="font-serif text-white text-[36px] md:text-[54px] leading-[1.05] tracking-tight">
              Nishit Thakkar
            </h2>
            <p className="mt-3 text-amber-200 font-serif italic text-[18px]">
              Manifestation Coach
            </p>
            <p className="mt-6 text-neutral-400 text-[15px] md:text-[16px] leading-relaxed">
              With over 15 years of professional experience exploring human potential through
              astrology, vastu, and numerology, Nishit Thakkar has helped thousands understand the
              subconscious mind and its role in creating reality.
            </p>
            <p className="mt-4 text-neutral-400 text-[15px] md:text-[16px] leading-relaxed">
              His work draws from ancient wisdom and modern psychology — turning abstract spiritual
              ideas into grounded, repeatable practices.
            </p>
          </div>
          <div className="order-1 md:order-2">
            <div className="relative mx-auto w-full max-w-md">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-amber-300/20 via-transparent to-transparent blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-navy-900">
                <img
                  src={SITE.authorImage}
                  alt="Nishit Thakkar — Manifestation Coach"
                  className="w-full h-[520px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
